import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../config';
import SharedNav from './SharedNav';
import './MessageTemplates.css';

function TemplateEditor({ templateKey, title, description }) {
  const [content, setContent] = useState('');
  const [activeContent, setActiveContent] = useState('');
  const [promotedAt, setPromotedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/sms-template`, { ...authHeaders(), params: { key: templateKey } });
      setContent(data.content || '');
      setActiveContent(data.active_content || '');
      setPromotedAt(data.promoted_at);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load template');
    } finally {
      setLoading(false);
    }
  }, [templateKey]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const { data } = await axios.put(`${API_URL}/api/sms-template`, { content }, { ...authHeaders(), params: { key: templateKey } });
      setMessage(data.message);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const preview = content
    .replace(/\{\{\s*candidate_name\s*\}\}/g, 'Earl Ratcliff')
    .replace(/\{\{\s*recruiter_first_name\s*\}\}/g, 'Ash')
    .replace(/\{\{\s*recruiter_name\s*\}\}/g, 'Ash Rathmore');

  return (
    <section className="mt-section">
      <h2>{title}</h2>
      <p className="message-templates-intro">
        {description} Use <code>{'{{candidate_name}}'}</code>, <code>{'{{recruiter_name}}'}</code>, or <code>{'{{recruiter_first_name}}'}</code> as placeholders.
      </p>

      {loading ? (
        <div>Loading…</div>
      ) : (
        <>
          <div className="mt-box">
            <label>Draft (edit anytime)</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={5}
            />
            <div className="mt-preview">
              <strong>Preview:</strong> {preview || <em>Nothing to preview yet</em>}
            </div>
            <button className="mt-save-btn" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save Draft'}
            </button>
            {message && <div className="mt-success">{message}</div>}
            {error && <div className="mt-error">{error}</div>}
          </div>

          <div className="mt-box mt-active-box">
            <label>Currently Active (what's actually being sent right now)</label>
            <p className="mt-active-text">{activeContent || <em>Nothing active yet</em>}</p>
            <small>
              {promotedAt
                ? `Last promoted: ${new Date(promotedAt).toLocaleString()}`
                : 'Not promoted yet — this becomes active after the next daily refresh (1pm IST).'}
            </small>
          </div>
        </>
      )}
    </section>
  );
}

export default function MessageTemplates({ user, onLogout }) {
  return (
    <div className="message-templates-page">
      <SharedNav user={user} onLogout={onLogout} />
      <div className="message-templates-content">
        <TemplateEditor
          templateKey="intro_text"
          title="Intro Text Template"
          description="This is the message sent to every candidate rated 5+ automatically."
        />
        <TemplateEditor
          templateKey="fadv_sent"
          title="FADV Sent Template"
          description="This is the message sent to a candidate as soon as their FADV status is set to Sent."
        />
        <TemplateEditor
          templateKey="drug_physical"
          title="Drug and Physical Template"
          description="This is the message sent to a candidate as soon as their drug test status is set to Pending."
        />
      </div>
    </div>
  );
}
